import React, { useEffect, useRef } from 'react';
import { Chart, registerables, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend, ChartDataset } from 'chart.js';
import '../../constants/const';
import { COLOR_CODE, LINE_SEQ } from '../../constants/const';

Chart.register(...registerables, LineController, LineElement, PointElement, LinearScale, CategoryScale, Title, Tooltip, Legend);

interface pitProps {
  dayCount: number;
  lineDatas: number[][];
  barDatas: number[][];
  lineTitles: string[];
  barTitles: string[];
  isSelect: boolean[];
  exist?: boolean[];
}

const COLOR_LINE_WIDTH: number = 2;
const FONT_SIZE_14: number = 14;
const months: number[] = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

const COLOR_ARRAY: string[] = [
  COLOR_CODE.INITIAL,
  COLOR_CODE.PLAN,
  COLOR_CODE.RESULT,
  COLOR_CODE.DRAFT1,
  COLOR_CODE.DRAFT2,
  COLOR_CODE.DRAFT3,
  COLOR_CODE.DRAFT4,
  COLOR_CODE.DRAFT5,
];

function getLeftMaxLimit(values: number[][]): number {
  let maxValue: number = 0;
  for (let i = 0; i < values.length; i++) {
    maxValue = maxValue > Math.max(...values[i]) ? maxValue : Math.max(...values[i]);
  }
  return calcMathCeil(maxValue);
}

function getRightMaxLimit(values: number[][]): number {
  let maxValue: number = 0;
  for (let i = 0; i < values.length; i++) {
    maxValue = maxValue > Math.max(...values[i]) ? maxValue : Math.max(...values[i]);
  }
  return calcMathCeil(maxValue) * 2;
}

/**
 * 与えられた値に対して、最上位-1桁から切り上げた数値を返す
 * また与えられた値に対して切り上げが起きない場合も、最上位の桁に+1した数値を返す
 * 1以下なら1にする
 * @param value
 * @returns
 * @example
 * input:100 return:200
 * input:150 return:200
 * input:99.9 return 200 (小数ははじめに切り上げられる)
 */
function calcMathCeil(value: number) {
  if (value >= 1) {
    const num: number = Math.ceil(value);
    const numLength: number = String(num).length;
    const val1: number = num / 10 ** (numLength - 1);
    const val2: number = Math.ceil(val1);
    if (val1 === val2) {
      return (val2 + 1) * 10 ** (numLength - 1);
    } else {
      return val2 * 10 ** (numLength - 1);
    }
  } else {
    return 1;
  }
}

const MixedChart: React.FC<pitProps> = (pitProps) => {
  const chartRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const ctx = chartRef.current?.getContext('2d');

    if (ctx) {
      const tempLines: ChartDataset[] = [];
      for (let num = 0; num < pitProps.lineDatas.length; num++) {
        let isSet: boolean = true;
        if (pitProps.exist && (num === LINE_SEQ.INITIAL || num === LINE_SEQ.PLAN || num === LINE_SEQ.RESULT)) {
          if (!pitProps.exist[num]) {
            isSet = false;
          }
        } else if (pitProps.exist && num >= LINE_SEQ.DRAFT1 && pitProps.exist.length >= LINE_SEQ.DRAFT1) {
          if (!pitProps.exist[LINE_SEQ.DRAFT1]) {
            isSet = false;
          }
        }
        if (isSet) {
          tempLines.push({
            type: 'line',
            label: pitProps.lineTitles[num],
            data: pitProps.lineDatas[num],
            borderColor: COLOR_ARRAY[num],
            backgroundColor: COLOR_ARRAY[num],
            borderWidth: COLOR_LINE_WIDTH,
            tension: 0.1,
            pointRadius: 5,
            hidden: !pitProps.isSelect[num],
            yAxisID: 'y',
            pointStyle: num === LINE_SEQ.PLAN ? 'rectRot' : num === LINE_SEQ.RESULT ? 'crossRot' : 'triangle',
          });
        }
      }

      // let maxBarValue: number = 0;
      const tempBars: ChartDataset[] = [];
      for (let num = 0; num < pitProps.barDatas.length; num++) {
        let isSet: boolean = true;
        if (pitProps.exist && (num === LINE_SEQ.PLAN || num === LINE_SEQ.RESULT)) {
          if (!pitProps.exist[num]) {
            isSet = false;
          }
        } else if (pitProps.exist && num >= LINE_SEQ.DRAFT1 && pitProps.exist.length >= LINE_SEQ.DRAFT1) {
          if (!pitProps.exist[LINE_SEQ.DRAFT1]) {
            isSet = false;
          }
        }
        if (isSet) {
          tempBars.push({
            type: 'bar',
            label: pitProps.barTitles[num],
            data: pitProps.barDatas[num],
            borderColor: COLOR_ARRAY[num],
            borderWidth: COLOR_LINE_WIDTH,
            hidden: !pitProps.isSelect[num],
            barPercentage: 0.3,
            yAxisID: 'y1',
          });
          // if (maxBarValue < Math.max(...pitProps.barDatas[num])) {
          //   maxBarValue = Math.max(...pitProps.barDatas[num]);
          // }
        }
      }
      // maxBarValue = roundUpToNextPlace(maxBarValue);

      const labelStr: string[] = [];

      for (let i = 0; i < pitProps.dayCount; i++) {
        if (pitProps.dayCount === months.length) {
          labelStr.push(String(months[i]).concat('月'));
        } else {
          labelStr.push(String(i + 1).concat('日'));
        }
      }

      const mixedChart = new Chart(ctx, {
        type: 'bar', // ベースを'bar'に設定
        data: {
          labels: labelStr,
          datasets: [...tempLines, ...tempBars],
        },
        options: {
          plugins: {
            legend: {
              position: 'top',
              fullSize: false,
              labels: {
                generateLabels: (chart: Chart) => {
                  return Chart.defaults.plugins.legend.labels.generateLabels(chart).filter((item) => {
                    if (item.datasetIndex === undefined) {
                      return false;
                    }
                    const ds = chart.data.datasets[item.datasetIndex];
                    return ds.type !== 'bar';
                  });
                },
              },
            },
          },
          maintainAspectRatio: false,
          responsive: true, // レスポンシブ対応
          scales: {
            y: {
              // 左のY軸の設定（搬入量）
              type: 'linear',
              position: 'left',
              title: {
                display: true,
              },
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
              },
              max: getLeftMaxLimit(pitProps.lineDatas),
            },
            y1: {
              // 右のY軸の設定（トラックの台数）
              type: 'linear',
              position: 'right',
              title: {
                display: true,
              },
              grid: {
                drawOnChartArea: false, // 左のY軸との重なりを防ぐ
              },
              max: getRightMaxLimit(pitProps.barDatas),
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
                padding: 0,
                stepSize: 1,
                maxTicksLimit: 10, // メモリを最大10個までに制限
                // callback: function (value) {
                //   return `${value}[台]`;
                // },
              },
            },
            x: {
              ticks: {
                font: {
                  size: FONT_SIZE_14,
                },
                maxRotation: 0,
                minRotation: 0,
              },
            },
          },
        },
        plugins: [
          {
            id: 'unitDraw',
            afterDraw: (chart) => {
              const ctx = chart.ctx;
              ctx.save();
              ctx.font = `bold ${FONT_SIZE_14}px`;
              ctx.fillStyle = chart?.options?.scales?.y?.ticks?.color?.toString() || '#666';

              // 左Y軸の単位
              if (chart.scales.y) {
                ctx.textAlign = 'left';
                ctx.textBaseline = 'bottom';
                ctx.fillText('単位：[t]', chart.scales.y.left, chart.chartArea.top - FONT_SIZE_14);
              }

              // 右Y軸の単位
              if (chart.scales.y1) {
                ctx.textAlign = 'right';
                ctx.textBaseline = 'bottom';
                ctx.fillText('単位：台', chart.scales.y1.right, chart.chartArea.top - FONT_SIZE_14);
              }
              ctx.restore();
            },
          },
        ],
      });

      return () => {
        mixedChart.destroy(); // クリーンアップのためにチャートを破棄
      };
    }
  }, [pitProps.dayCount, pitProps.lineDatas, pitProps.barDatas, pitProps.lineTitles, pitProps.barTitles, pitProps.isSelect]);
  // LineChartと合わせている
  return <canvas ref={chartRef} height={'320px'} width={'1180px'} />;
};

export default MixedChart;
